"use client"

import * as React from "react"
import { parseDate } from "chrono-node"
import { CalendarIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DateRange {
  from?: Date | string
  to?: Date | string
}

interface SmartDatePickerProps {
  value?: DateRange
  onChange?: (range: DateRange) => void
  className?: string
}

function formatDate(date: Date | undefined) {
  if (!date) {
    return ""
  }

  // Simple format without any timezone conversions - just use the date as-is
  const year = date.getFullYear()
  const month = date.toLocaleDateString("en-US", { month: "short" })
  const day = String(date.getDate()).padStart(2, '0')
  
  return `${day} ${month} ${year}`
}

export function SmartDateInput({ 
  id, 
  label, 
  value, 
  onChange, 
  placeholder,
  fromDate
}: {
  id: string
  label: string
  value?: Date | string
  onChange?: (date?: Date | string) => void
  placeholder?: string
  fromDate?: Date | string
}) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [month, setMonth] = React.useState<Date | undefined>(value instanceof Date ? value : new Date())

  // Check if a string is a relative date format (d0, d-1, d-20, etc.)
  const isRelativeDateFormat = (text: string): boolean => {
    return /^d-?\d+$/.test(text)
  }

  // Check if a string is an absolute date format (YYYY-MM-DD)
  const isAbsoluteDateFormat = (text: string): boolean => {
    return /^\d{4}-\d{2}-\d{2}$/.test(text)
  }

  // Update input value when value prop changes
  React.useEffect(() => {
    if (value) {
      if (typeof value === 'string' && isRelativeDateFormat(value)) {
        // Relative date format (d0, d-1, etc.)
        setInputValue(value)
        setMonth(new Date()) // Use today for calendar display
      } else if (typeof value === 'string' && isAbsoluteDateFormat(value)) {
        // Absolute date format (YYYY-MM-DD) - display as-is and parse for calendar
        setInputValue(value)
        const dateValue = new Date(value + 'T00:00:00')
        setMonth(dateValue)
      } else {
        // Regular Date object
        const dateValue = value instanceof Date ? value : new Date()
        setInputValue(formatDate(dateValue))
        setMonth(dateValue)
      }
    } else {
      setInputValue("")
      setMonth(undefined)
    }
  }, [value])

  const handleInputChange = (text: string) => {
    setInputValue(text)
    // Don't auto-parse while typing - only on blur or Enter
  }

  const handleInputBlur = () => {
    tryParseDate(inputValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      tryParseDate(inputValue)
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      setOpen(true)
    }
  }

  const tryParseDate = (text: string) => {
    // If input is empty, clear the date
    if (!text.trim()) {
      onChange?.(undefined)
      return
    }

    // Check if the input is a relative date format (d0, d-1, d-20, etc.)
    if (isRelativeDateFormat(text)) {
      // Pass relative date format directly without conversion
      onChange?.(text)
      return
    }

    // Check if the input is an absolute date format (YYYY-MM-DD)
    if (isAbsoluteDateFormat(text)) {
      // Pass absolute date format directly as string
      onChange?.(text)
      const dateValue = new Date(text + 'T00:00:00')
      setMonth(dateValue)
      return
    }

    // For regular text input, try natural language parsing
    if (text.length > 2) {
      const parsedDate = parseDate(text)
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        // Validate the parsed date makes sense
        const now = new Date()
        const tenYearsAgo = new Date(now.getFullYear() - 10, 0, 1)
        const tenYearsFromNow = new Date(now.getFullYear() + 10, 11, 31)

        if (parsedDate >= tenYearsAgo && parsedDate <= tenYearsFromNow) {
          onChange?.(parsedDate)
          setMonth(parsedDate)
        }
      }
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <Label htmlFor={id} className="text-xs">
          {label}
        </Label>
      )}
      <div className="relative">
        <Input
          id={id}
          value={inputValue}
          placeholder={placeholder}
          className="bg-background pr-10 text-sm"
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-1/2 right-2 h-6 w-6 -translate-y-1/2 p-0"
            >
              <CalendarIcon className="h-3.5 w-3.5" />
              <span className="sr-only">Select {label.toLowerCase()}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="end">
            <Calendar
              mode="single"
              selected={value instanceof Date ? value : undefined}
              captionLayout="dropdown"
              month={month}
              onMonthChange={setMonth}
              fromDate={fromDate instanceof Date ? fromDate : undefined}
              onSelect={(date) => {
                onChange?.(date)
                setInputValue(date ? formatDate(date) : "")
                setOpen(false)
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

export function SmartDatePicker({ value, onChange, className }: SmartDatePickerProps) {
  const handleStartDateChange = (date?: Date | string) => {
    onChange?.({ from: date, to: value?.to })
  }

  const handleEndDateChange = (date?: Date | string) => {
    onChange?.({ from: value?.from, to: date })
  }

  return (
    <div className={className}>
      <Label className="text-sm font-medium">Period</Label>
      <div className="space-y-4 mt-2">
        <SmartDateInput
          id="start-date"
          label="Start Date"
          value={value?.from}
          onChange={handleStartDateChange}
          placeholder="yesterday, last week, 2024-01-01, d-1"
        />
        <SmartDateInput
          id="end-date"
          label="End Date"
          value={value?.to}
          onChange={handleEndDateChange}
          placeholder="today, tomorrow, 2024-12-31, d0"
          fromDate={value?.from}
        />
      </div>
    </div>
  )
}